import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import {
  Globe,
  Network,
  Shield,
  ChevronRight,
  Server,
  Smartphone,
  Monitor,
  Cloud,
  Users,
  Star
} from 'lucide-react';

/* ===================== Global Styles ===================== */
const GlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    background: #ffffff;
    color: #1e293b;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow: hidden;
  }
`;

/* ===================== Layout Components ===================== */
const PageContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
`;

const Header = styled.header`
  padding: 1rem 2rem;
  background: white;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 1rem 2rem;
  overflow: hidden;
`;

const ScanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  height: calc(100vh - 120px);
  overflow: hidden;
`;

const ScanColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  padding-right: 0.5rem;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #e5e7eb;
    border-radius: 3px;
  }
`;

/* ===================== Card Components ===================== */
const ScanCard = styled.div`
  position: relative;
  background: white;
  border: 1px solid ${props => props.isSelected ? props.borderColor : '#e5e7eb'};
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    border-color: ${props => props.borderColor};
  }

  ${props => props.isSelected && `
    border-color: ${props.borderColor};
    box-shadow: 0 0 0 2px ${props.borderColor}20;
  `}
`;

const PremiumBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: linear-gradient(135deg, #fcd34d, #f59e0b);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background: ${props => props.iconBg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor};
`;

const CardContent = styled.div`
  flex-grow: 1;
  min-width: 0;
  padding-right: ${props => props.premium ? '2.5rem' : '0'};
`;

const ScanTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const ComplexityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1875rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  background: ${props => props.bg};
  color: ${props => props.color};
`;

const ScanDescription = styled.p`
  color: #6b7280;
  font-size: 0.8125rem;
  line-height: 1.4;
  margin-bottom: 0.75rem;
`;

const FeaturesList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
`;

const FeatureItem = styled.li`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: #4b5563;
  background: #f3f4f6;
  font-weight: 500;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
`;

const ConfigureButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${props => props.color};
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;

  &:hover {
    background: ${props => props.hoverBg};
  }
`;

/* ===================== Main Component ===================== */
export default function AdvancedScanPage() {
  const [selectedScan, setSelectedScan] = useState(null);
  const navigate = useNavigate();

  const handleConfigureClick = (scanType) => {
    navigate(`/pre-engagement-form/${scanType}`);
  };

  const getComplexityStyle = (complexity) => {
    switch (complexity) {
      case 'Intermediate':
        return {
          bg: '#e0e7ff',
          color: '#4338ca'
        };
      case 'Advanced':
        return {
          bg: '#fef3c7',
          color: '#92400e'
        };
      case 'Expert':
        return {
          bg: '#fee2e2',
          color: '#991b1b'
        };
      default:
        return {
          bg: '#f3f4f6',
          color: '#4b5563'
        };
    }
  };

  const scanTypes = [
    {
      id: 'web-app',
      name: 'Web App Scan',
      icon: <Globe size={16} />,
      description: 'OWASP Top 10 security assessment with manual verification',
      features: ['XSS', 'SQLi', 'CSRF', 'Auth Bypass'],
      complexity: 'Intermediate',
      premium: false,
      colors: {
        borderColor: '#4f46e5',
        iconBg: '#e0e7ff',
        iconColor: '#4f46e5',
        buttonColor: '#4f46e5',
        buttonHoverBg: '#e0e7ff',
        featureDot: '#4f46e5'
      }
    },
    {
      id: 'network-infra',
      name: 'Network Scan',
      icon: <Network size={16} />,
      description: 'Network topology analysis with penetration testing',
      features: ['Port Scan', 'Service Detect', 'Network Map', 'Vuln Assess'],
      complexity: 'Intermediate',
      premium: false,
      colors: {
        borderColor: '#7c3aed',
        iconBg: '#ede9fe',
        iconColor: '#7c3aed',
        buttonColor: '#7c3aed',
        buttonHoverBg: '#ede9fe',
        featureDot: '#7c3aed'
      }
    },
    {
      id: 'api-security',
      name: 'API Testing',
      icon: <Server size={16} />,
      description: 'REST/GraphQL API security audit with manual testing',
      features: ['Endpoints', 'Auth Test', 'Data Validation', 'Rate Limits'],
      complexity: 'Intermediate',
      premium: false,
      colors: {
        borderColor: '#059669',
        iconBg: '#d1fae5',
        iconColor: '#059669',
        buttonColor: '#059669',
        buttonHoverBg: '#d1fae5',
        featureDot: '#059669'
      }
    },
    {
      id: 'firewall',
      name: 'Firewall Test',
      icon: <Shield size={16} />,
      description: 'Firewall rules and policy analysis with bypass testing',
      features: ['Rules', 'Policies', 'Security Gaps', 'Compliance'],
      complexity: 'Advanced',
      premium: false,
      colors: {
        borderColor: '#dc2626',
        iconBg: '#fee2e2',
        iconColor: '#dc2626',
        buttonColor: '#dc2626',
        buttonHoverBg: '#fee2e2',
        featureDot: '#dc2626'
      }
    },
    {
      id: 'thick-client',
      name: 'Thick Client',
      icon: <Monitor size={16} />,
      description: 'Desktop app security with reverse engineering',
      features: ['Binary', 'Memory', 'Storage', 'Comms'],
      complexity: 'Advanced',
      premium: true,
      colors: {
        borderColor: '#6366f1',
        iconBg: '#e0e7ff',
        iconColor: '#6366f1',
        buttonColor: '#6366f1',
        buttonHoverBg: '#e0e7ff',
        featureDot: '#6366f1'
      }
    },
    {
      id: 'thin-client',
      name: 'Thin Client',
      icon: <Globe size={16} />,
      description: 'Browser-based app security testing',
      features: ['Client-Side', 'Sessions', 'DOM', 'Exploits'],
      complexity: 'Advanced',
      premium: true,
      colors: {
        borderColor: '#06b6d4',
        iconBg: '#cffafe',
        iconColor: '#06b6d4',
        buttonColor: '#06b6d4',
        buttonHoverBg: '#cffafe',
        featureDot: '#06b6d4'
      }
    },
    {
      id: 'mobile-app',
      name: 'Mobile App',
      icon: <Smartphone size={16} />,
      description: 'iOS/Android security with runtime testing',
      features: ['Static', 'Dynamic', 'Runtime', 'Storage'],
      complexity: 'Expert',
      premium: true,
      colors: {
        borderColor: '#db2777',
        iconBg: '#fce7f3',
        iconColor: '#db2777',
        buttonColor: '#db2777',
        buttonHoverBg: '#fce7f3',
        featureDot: '#db2777'
      }
    },
    {
      id: 'red-teaming',
      name: 'Red Team',
      icon: <Users size={16} />,
      description: 'Full adversarial simulation with social engineering',
      features: ['Social Eng', 'Physical', 'Persistence', 'Lateral Move'],
      complexity: 'Expert',
      premium: true,
      colors: {
        borderColor: '#ea580c',
        iconBg: '#ffedd5',
        iconColor: '#ea580c',
        buttonColor: '#ea580c',
        buttonHoverBg: '#ffedd5',
        featureDot: '#ea580c'
      }
    },
    {
      id: 'cloud-pentest',
      name: 'Cloud Pentest',
      icon: <Cloud size={16} />,
      description: 'Cloud-native security with infrastructure testing',
      features: ['Containers', 'IAM', 'Config', 'Serverless'],
      complexity: 'Expert',
      premium: true,
      colors: {
        borderColor: '#7c3aed',
        iconBg: '#ede9fe',
        iconColor: '#7c3aed',
        buttonColor: '#7c3aed',
        buttonHoverBg: '#ede9fe',
        featureDot: '#7c3aed'
      }
    }
  ];

  // Split scan types into 3 columns for the grid
  const column1 = scanTypes.slice(0, 3);
  const column2 = scanTypes.slice(3, 6);
  const column3 = scanTypes.slice(6);

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        {/* <Header>
          <Title>Advanced Security Scans</Title>
          <Subtitle>Select a scan type to configure your assessment</Subtitle>
        </Header> */}
        <br/>
        <MainContent>
          <ScanGrid>
            <ScanColumn>
              {column1.map((scan) => {
                const complexityStyle = getComplexityStyle(scan.complexity);
                const isSelected = selectedScan === scan.id;

                return (
                  <ScanCard
                    key={scan.id}
                    onClick={() => setSelectedScan(scan.id)}
                    borderColor={scan.colors.borderColor}
                    isSelected={isSelected}
                  >
                    {scan.premium && (
                      <PremiumBadge>
                        <Star size={12} />
                        Premium
                      </PremiumBadge>
                    )}

                    <CardHeader>
                      <IconContainer
                        iconBg={scan.colors.iconBg}
                        iconColor={scan.colors.iconColor}
                      >
                        {scan.icon}
                      </IconContainer>
                      <CardContent premium={scan.premium}>
                        <ScanTitle>{scan.name}</ScanTitle>
                        <ComplexityBadge
                          bg={complexityStyle.bg}
                          color={complexityStyle.color}
                        >
                          {scan.complexity}
                        </ComplexityBadge>
                      </CardContent>
                    </CardHeader>

                    <ScanDescription>{scan.description}</ScanDescription>

                    <FeaturesList>
                      {scan.features.map((feature, index) => (
                        <FeatureItem key={index}>{feature}</FeatureItem>
                      ))}
                    </FeaturesList>

                    <CardFooter>
                      <ConfigureButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfigureClick(scan.id);
                        }}
                        color={scan.colors.buttonColor}
                        hoverBg={scan.colors.buttonHoverBg}
                      >
                        Configure
                        <ChevronRight size={14} />
                      </ConfigureButton>
                    </CardFooter>
                  </ScanCard>
                );
              })}
            </ScanColumn>

            <ScanColumn>
              {column2.map((scan) => {
                const complexityStyle = getComplexityStyle(scan.complexity);
                const isSelected = selectedScan === scan.id;

                return (
                  <ScanCard
                    key={scan.id}
                    onClick={() => setSelectedScan(scan.id)}
                    borderColor={scan.colors.borderColor}
                    isSelected={isSelected}
                  >
                    {scan.premium && (
                      <PremiumBadge>
                        <Star size={12} />
                        Premium
                      </PremiumBadge>
                    )}

                    <CardHeader>
                      <IconContainer
                        iconBg={scan.colors.iconBg}
                        iconColor={scan.colors.iconColor}
                      >
                        {scan.icon}
                      </IconContainer>
                      <CardContent premium={scan.premium}>
                        <ScanTitle>{scan.name}</ScanTitle>
                        <ComplexityBadge
                          bg={complexityStyle.bg}
                          color={complexityStyle.color}
                        >
                          {scan.complexity}
                        </ComplexityBadge>
                      </CardContent>
                    </CardHeader>

                    <ScanDescription>{scan.description}</ScanDescription>

                    <FeaturesList>
                      {scan.features.map((feature, index) => (
                        <FeatureItem key={index}>{feature}</FeatureItem>
                      ))}
                    </FeaturesList>

                    <CardFooter>
                      <ConfigureButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfigureClick(scan.id);
                        }}
                        color={scan.colors.buttonColor}
                        hoverBg={scan.colors.buttonHoverBg}
                      >
                        Configure
                        <ChevronRight size={14} />
                      </ConfigureButton>
                    </CardFooter>
                  </ScanCard>
                );
              })}
            </ScanColumn>

            <ScanColumn>
              {column3.map((scan) => {
                const complexityStyle = getComplexityStyle(scan.complexity);
                const isSelected = selectedScan === scan.id;

                return (
                  <ScanCard
                    key={scan.id}
                    onClick={() => setSelectedScan(scan.id)}
                    borderColor={scan.colors.borderColor}
                    isSelected={isSelected}
                  >
                    {scan.premium && (
                      <PremiumBadge>
                        <Star size={12} />
                        Premium
                      </PremiumBadge>
                    )}

                    <CardHeader>
                      <IconContainer
                        iconBg={scan.colors.iconBg}
                        iconColor={scan.colors.iconColor}
                      >
                        {scan.icon}
                      </IconContainer>
                      <CardContent premium={scan.premium}>
                        <ScanTitle>{scan.name}</ScanTitle>
                        <ComplexityBadge
                          bg={complexityStyle.bg}
                          color={complexityStyle.color}
                        >
                          {scan.complexity}
                        </ComplexityBadge>
                      </CardContent>
                    </CardHeader>

                    <ScanDescription>{scan.description}</ScanDescription>

                    <FeaturesList>
                      {scan.features.map((feature, index) => (
                        <FeatureItem key={index}>{feature}</FeatureItem>
                      ))}
                    </FeaturesList>

                    <CardFooter>
                      <ConfigureButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfigureClick(scan.id);
                        }}
                        color={scan.colors.buttonColor}
                        hoverBg={scan.colors.buttonHoverBg}
                      >
                        Configure
                        <ChevronRight size={14} />
                      </ConfigureButton>
                    </CardFooter>
                  </ScanCard>
                );
              })}
            </ScanColumn>
          </ScanGrid>
        </MainContent>
      </PageContainer>
    </>
  );
}